import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { PrivacyDialog } from "./PrivacyDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENTS = [
  { id: "start", label: "Non so da dove iniziare" },
  { id: "info", label: "Vorrei più informazioni sui servizi" },
  { id: "booking", label: "Voglio prenotare una consulenza" },
];

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  city: "",
  message: "",
};

export const ContactForm = () => {
  const [intent, setIntent] = useState("info");
  const [form, setForm] = useState(emptyForm);
  const [service, setService] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [company, setCompany] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // A service card / percorso can preselect itself and scroll here.
  useEffect(() => {
    const onPreselect = (e) => {
      const name = (e.detail || "").replace(/\n/g, " ").trim();
      setService(name);
      setIntent("booking");
      setDone(false);
    };
    window.addEventListener("preselect-service", onPreselect);
    return () => window.removeEventListener("preselect-service", onPreselect);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    if (!privacy) {
      toast.error("Devi accettare la Privacy Policy per continuare.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { intent, ...form, service, privacy, company });
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontatterò al più presto.");
      setForm(emptyForm);
      setService("");
      setPrivacy(false);
    } catch (err) {
      toast.error("Ops, qualcosa è andato storto. Riprova o scrivimi su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="bg-cipria/60 border border-espresso/10 p-10 sm:p-14 text-center"
        data-testid="form-success"
      >
        <div className="w-14 h-14 rounded-full bg-fuchsiabrand flex items-center justify-center mx-auto mb-6">
          <Check className="text-white" size={26} />
        </div>
        <h3 className="font-serif text-3xl sm:text-4xl text-espresso mb-3">Grazie!</h3>
        <p className="text-stone leading-relaxed max-w-sm mx-auto">
          Ho ricevuto la tua richiesta. Ti ricontatterò a breve per verificare la
          disponibilità e raccontarti tutto di persona.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid="form-reset"
          className="mt-8 font-mono text-xs uppercase tracking-[0.15em] text-fuchsiabrand hover:text-magentadark transition-colors"
        >
          Invia un'altra richiesta
        </button>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white/40 backdrop-blur-md border border-espresso/10 p-6 sm:p-9"
      data-testid="contact-form"
    >
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone mb-4">
        01 — Da dove vuoi partire?
      </p>
      <div className="flex flex-wrap gap-2.5 mb-8">
        {INTENTS.map((it) => {
          const active = intent === it.id;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => setIntent(it.id)}
              data-testid={`intent-${it.id}`}
              className={`px-4 py-2.5 rounded-full text-sm border transition-all duration-300 ${
                active
                  ? "bg-fuchsiabrand text-white border-fuchsiabrand"
                  : "bg-transparent text-stone border-espresso/20 hover:border-fuchsiabrand hover:text-fuchsiabrand"
              }`}
            >
              {it.label}
            </button>
          );
        })}
      </div>

      {service && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 mb-8 -mt-3"
          data-testid="selected-service-chip"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone">
            Servizio:
          </span>
          <span className="inline-flex items-center gap-2 bg-fuchsiabrand/10 text-fuchsiabrand border border-fuchsiabrand/30 rounded-full pl-3 pr-2 py-1.5 text-sm font-medium">
            {service}
            <button
              type="button"
              onClick={() => setService("")}
              data-testid="clear-service"
              aria-label="Rimuovi servizio selezionato"
              className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-fuchsiabrand/20 transition-colors"
            >
              <X size={12} />
            </button>
          </span>
        </motion.div>
      )}

      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6 mb-6">
        <input
          className="line-input"
          placeholder="Nome e Cognome *"
          value={form.name}
          onChange={update("name")}
          data-testid="input-name"
        />
        <input
          className="line-input"
          type="email"
          placeholder="Email *"
          value={form.email}
          onChange={update("email")}
          data-testid="input-email"
        />
        <input
          className="line-input"
          placeholder="Telefono (WhatsApp) *"
          value={form.phone}
          onChange={update("phone")}
          data-testid="input-phone"
        />
        <input
          className="line-input"
          placeholder="Città / Zona di residenza *"
          value={form.city}
          onChange={update("city")}
          data-testid="input-city"
        />
      </div>

      <textarea
        className="line-input resize-none mb-7"
        rows={3}
        placeholder="Hai già fatto percorsi d'immagine in passato? Raccontami qualcosa di più su di te."
        value={form.message}
        onChange={update("message")}
        data-testid="input-message"
      />

      {/* Honeypot: hidden from real users */}
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />

      <label className="flex items-start gap-3 mb-7 cursor-pointer group" data-testid="privacy-label">
        <span
          onClick={() => setPrivacy((v) => !v)}
          className={`mt-0.5 w-5 h-5 flex-shrink-0 border flex items-center justify-center transition-colors duration-300 ${
            privacy ? "bg-fuchsiabrand border-fuchsiabrand" : "border-espresso/30 group-hover:border-fuchsiabrand"
          }`}
          data-testid="privacy-checkbox"
          role="checkbox"
          aria-checked={privacy}
        >
          {privacy && <Check size={14} className="text-white" />}
        </span>
        <span className="text-xs text-stone leading-relaxed">
          Ho letto e accetto la{" "}
          <PrivacyDialog>
            <button
              type="button"
              data-testid="open-privacy"
              className="text-fuchsiabrand underline underline-offset-2 hover:text-magentadark"
            >
              Privacy Policy
            </button>
          </PrivacyDialog>{" "}
          e acconsento al trattamento dei miei dati per essere ricontattata. *
        </span>
      </label>

      <button
        type="submit"
        disabled={loading}
        data-testid="submit-button"
        className="group w-full bg-fuchsiabrand text-white py-4 px-6 rounded-full font-mono text-xs sm:text-sm uppercase tracking-[0.15em] flex items-center justify-center gap-3 hover:bg-magentadark transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="animate-spin" size={18} />
        ) : (
          <>
            Richiedi informazioni
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </>
        )}
      </button>
    </motion.form>
  );
};
